<template>
	<section v-loading="loading" class="provider-application-page">
		<div class="page-heading">
			<div>
				<h2>商家申请</h2>
				<p>{{ showApplyDetail ? '查看店铺申请信息及平台审核进度' : '填写真实有效的店铺信息，提交后由平台进行审核' }}</p>
			</div>
			<el-button v-if="canResubmit" type="primary" icon="el-icon-refresh" @click="handleResubmit">重新提交申请</el-button>
		</div>

		<template v-if="showApplyDetail">
			<div class="audit-card">
				<div class="audit-title-row">
					<div>
						<h3>审核进度</h3>
						<p>申请提交后，平台会尽快完成资料审核</p>
					</div>
					<span :class="['audit-state', auditStateClass]">{{ auditStateText }}</span>
				</div>
				<div class="audit-progress">
					<div class="progress-item is-complete">
						<span class="progress-icon"><i class="el-icon-document-checked"></i></span>
						<strong>提交申请</strong>
						<small>{{ applyInfo.createTime || applyInfo.applyTime || '已完成' }}</small>
					</div>
					<span class="progress-line" :class="{ 'is-complete': Number(applyInfo.auditState) === 1 }"></span>
					<div :class="['progress-item', Number(applyInfo.auditState) === 0 ? 'is-current' : 'is-complete']">
						<span class="progress-icon"><i class="el-icon-search"></i></span>
						<strong>平台审核</strong>
						<small>{{ Number(applyInfo.auditState) === 0 ? '审核处理中' : '审核已完成' }}</small>
					</div>
					<span class="progress-line" :class="{ 'is-complete': Number(applyInfo.auditState) === 1 }"></span>
					<div :class="['progress-item', Number(applyInfo.auditState) === 1 ? 'is-complete' : 'is-waiting']">
						<span class="progress-icon"><i class="el-icon-circle-check"></i></span>
						<strong>开通店铺</strong>
						<small>{{ Number(applyInfo.auditState) === 1 ? '店铺已开通' : '等待审核通过' }}</small>
					</div>
				</div>
				<div v-if="Number(applyInfo.auditState) === 2 || auditOpinion" class="audit-opinion">
					<i class="el-icon-warning-outline"></i>
					<div>
						<strong>审核意见</strong>
						<p>{{ auditOpinion || '申请资料未通过审核，请完善后重新提交。' }}</p>
					</div>
				</div>
			</div>

			<div class="application-info-card">
				<div class="application-info-heading">
					<div class="heading-main">
						<span class="heading-icon"><i class="el-icon-document"></i></span>
						<div>
							<strong>申请信息</strong>
							<small>您本次提交的店铺入驻资料</small>
						</div>
					</div>
				</div>
				<div class="info-grid">
					<div class="info-item">
						<span>店铺名称</span>
						<strong>{{ applyInfo.shopName || '-' }}</strong>
					</div>
					<div class="info-item">
						<span>联系人</span>
						<strong>{{ applyInfo.contactPerson || applyInfo.contactName || '-' }}</strong>
					</div>
					<div class="info-item">
						<span>联系方式</span>
						<strong>{{ applyInfo.contactPhone || applyInfo.telephone || '-' }}</strong>
					</div>
					<div class="info-item">
						<span>经营主体名称</span>
						<strong>{{ applyInfo.companyName || applyInfo.subjectName || '-' }}</strong>
					</div>
					<div class="info-item is-full">
						<span>经营地址</span>
						<strong>{{ applyInfo.businessAddress || applyInfo.address || '-' }}</strong>
					</div>
					<div class="info-item is-full">
						<span>业务简介</span>
						<strong>{{ applyInfo.businessDesc || applyInfo.businessScope || '-' }}</strong>
					</div>
					<div class="info-item">
						<span>开户行名称</span>
						<strong>{{ applyInfo.bankName || '-' }}</strong>
					</div>
					<div class="info-item">
						<span>开户行账号</span>
						<strong>{{ applyInfo.bankAccount || '-' }}</strong>
					</div>
				</div>
			</div>
		</template>

		<el-form v-else ref="applicationForm" :model="formData" :rules="formRules" label-position="top" class="application-form">
			<div class="form-section">
				<div class="application-form-heading">
					<span>基础信息</span>
					<small>填写店铺及经营主体资料</small>
				</div>
				<el-row :gutter="28">
					<el-col :span="12">
						<el-form-item label="店铺名称" prop="shopName">
							<el-input v-model.trim="formData.shopName" maxlength="50" show-word-limit placeholder="请输入店铺名称"></el-input>
						</el-form-item>
					</el-col>
					<el-col :span="12">
						<el-form-item label="联系人" prop="contactPerson">
							<el-input v-model.trim="formData.contactPerson" maxlength="20" placeholder="请输入联系人"></el-input>
						</el-form-item>
					</el-col>
					<el-col :span="12">
						<el-form-item label="联系方式" prop="contactPhone">
							<el-input v-model.trim="formData.contactPhone" maxlength="20" placeholder="请输入手机号或座机号"></el-input>
						</el-form-item>
					</el-col>
					<el-col :span="12">
						<el-form-item label="经营主体名称" prop="companyName">
							<el-input v-model.trim="formData.companyName" maxlength="80" placeholder="请输入经营主体名称"></el-input>
						</el-form-item>
					</el-col>
					<el-col :span="24">
						<el-form-item label="经营地址" prop="businessAddress">
							<el-input v-model.trim="formData.businessAddress" maxlength="120" placeholder="请输入经营地址"></el-input>
						</el-form-item>
					</el-col>
					<el-col :span="24">
						<el-form-item label="业务简介" prop="businessDesc">
							<el-input
								v-model.trim="formData.businessDesc"
								type="textarea"
								:rows="5"
								maxlength="800"
								show-word-limit
								resize="none"
								placeholder="请输入店铺业务简介，不超过800字"
							></el-input>
						</el-form-item>
					</el-col>
				</el-row>
			</div>

			<div class="form-section">
				<div class="application-form-heading">
					<span>结算信息</span>
					<small>填写店铺结算账户信息</small>
				</div>
				<el-row :gutter="28">
					<el-col :span="12">
						<el-form-item label="开户行名称" prop="bankName">
							<el-input v-model.trim="formData.bankName" maxlength="80" placeholder="请输入开户行名称"></el-input>
						</el-form-item>
					</el-col>
					<el-col :span="12">
						<el-form-item label="开户行账号" prop="bankAccount">
							<el-input v-model.trim="formData.bankAccount" maxlength="40" placeholder="请输入开户行账号"></el-input>
						</el-form-item>
					</el-col>
				</el-row>
			</div>

			<div class="form-section">
				<div class="application-form-heading">
					<span>附件资料</span>
					<small>营业执照为必填项</small>
				</div>
				<el-row :gutter="28">
					<el-col :span="12">
						<el-form-item label="店铺 Logo">
							<upload-images
								v-model="logoFiles"
								:action="uploadAction"
								:data="logoUploadData"
								:headers="uploadHeaders"
								:preview-url="previewUrl"
								:delete-api="deleteApi"
								:delete-params="logoUploadData"
								:limit="1"
								:size="5"
								:tip="true"
							></upload-images>
						</el-form-item>
					</el-col>
					<el-col :span="12">
						<el-form-item label="营业执照" prop="businessLicenseFiles">
							<upload-images
								v-model="businessLicenseFiles"
								:action="uploadAction"
								:data="businessLicenseUploadData"
								:headers="uploadHeaders"
								:preview-url="previewUrl"
								:delete-api="deleteApi"
								:delete-params="businessLicenseUploadData"
								:limit="1"
								:size="5"
								:tip="true"
							></upload-images>
						</el-form-item>
					</el-col>
				</el-row>
			</div>

			<div class="form-actions">
				<el-button v-if="canCancelResubmit" @click="showApplyDetail = true">取消</el-button>
				<el-button type="primary" :loading="submitLoading" @click="handleSubmit">提交申请</el-button>
			</div>
		</el-form>
	</section>
</template>

<script>
	import { mapGetters } from 'vuex'
	import UploadImages from '@/components/UploadImages'
	import { deleteMerchantFile, previewMerchantFile, submitMerchantShopApply, uploadMerchantFile } from '@/api/mall'
	import { ENUM_SHOP_AUDIT_STATE, SHOP_APPLY_TYPE, SHOP_AUDIT_STATE } from '@/enum'
	import { getUuid } from '@/utils'
	import { getToken } from '@/utils/auth'

	const createFormData = () => ({
		id: getUuid(),
		shopName: '',
		contactPerson: '',
		contactPhone: '',
		companyName: '',
		businessAddress: '',
		businessDesc: '',
		bankName: '',
		bankAccount: '',
		businessLicenseFiles: []
	})

	export default {
		name: 'ProviderApplication',
		components: { UploadImages },
		data() {
			return {
				loading: false,
				submitLoading: false,
				showApplyDetail: false,
				formData: createFormData(),
				logoFiles: [],
				businessLicenseFiles: [],
				uploadAction: uploadMerchantFile(),
				previewUrl: previewMerchantFile(),
				deleteApi: deleteMerchantFile,
				formRules: {
					shopName: [{ required: true, message: '请输入店铺名称', trigger: 'blur' }],
					contactPerson: [{ required: true, message: '请输入联系人', trigger: 'blur' }],
					contactPhone: [
						{ required: true, message: '请输入联系方式', trigger: 'blur' },
						{ pattern: /^1[3-9]\d{9}$|^0\d{2,3}-?\d{7,8}$/, message: '请输入正确的手机号或座机号', trigger: 'blur' }
					],
					companyName: [{ required: true, message: '请输入经营主体名称', trigger: 'blur' }],
					businessAddress: [{ required: true, message: '请输入经营地址', trigger: 'blur' }],
					businessDesc: [
						{ required: true, message: '请输入业务简介', trigger: 'blur' },
						{ max: 800, message: '业务简介不能超过800字', trigger: 'blur' }
					],
					bankName: [{ required: true, message: '请输入开户行名称', trigger: 'blur' }],
					bankAccount: [{ required: true, message: '请输入开户行账号', trigger: 'blur' }],
					businessLicenseFiles: [{ validator: this.validateBusinessLicense, trigger: 'change' }]
				}
			}
		},
		computed: {
			...mapGetters(['merchantLatestApply']),
			applyInfo() {
				return this.merchantLatestApply || {}
			},
			auditStateText() {
				return ENUM_SHOP_AUDIT_STATE[this.applyInfo.auditState] || '待提交'
			},
			auditStateClass() {
				if (Number(this.applyInfo.auditState) === SHOP_AUDIT_STATE.PASS) return 'is-pass'
				if (Number(this.applyInfo.auditState) === SHOP_AUDIT_STATE.REJECT) return 'is-reject'
				return 'is-pending'
			},
			auditOpinion() {
				return this.applyInfo.auditOpinion || this.applyInfo.auditRemark || this.applyInfo.remark || ''
			},
			canResubmit() {
				return this.showApplyDetail && Number(this.applyInfo.auditState) === SHOP_AUDIT_STATE.REJECT
			},
			canCancelResubmit() {
				return Boolean(this.applyInfo.id)
			},
			uploadHeaders() {
				return { InternetToken: getToken() }
			},
			logoUploadData() {
				return { businessId: this.formData.id, fileBusinessType: 'SHOP_LOGO' }
			},
			businessLicenseUploadData() {
				return { businessId: this.formData.id, fileBusinessType: 'BUSINESS_LICENSE' }
			}
		},
		watch: {
			businessLicenseFiles() {
				this.formData.businessLicenseFiles = this.businessLicenseFiles
				this.$nextTick(() => {
					this.$refs.applicationForm && this.$refs.applicationForm.validateField('businessLicenseFiles')
				})
			}
		},
		created() {
			this.initPage()
		},
		methods: {
			validateBusinessLicense(rule, value, callback) {
				if (!this.businessLicenseFiles.length) callback(new Error('请上传营业执照'))
				else callback()
			},
			async initPage() {
				this.loading = true
				try {
					await this.$store.dispatch('merchantShop/loadShopContext', true)
					this.showApplyDetail = Boolean(this.applyInfo.id)
				} finally {
					this.loading = false
				}
			},
			fillForm(data) {
				this.formData = {
					...createFormData(),
					shopName: data.shopName || '',
					contactPerson: data.contactPerson || data.contactName || '',
					contactPhone: data.contactPhone || data.telephone || '',
					companyName: data.companyName || data.subjectName || '',
					businessAddress: data.businessAddress || data.address || '',
					businessDesc: data.businessDesc || data.businessScope || '',
					bankName: data.bankName || '',
					bankAccount: data.bankAccount || ''
				}
				this.logoFiles = []
				this.businessLicenseFiles = []
			},
			handleResubmit() {
				this.fillForm(this.applyInfo)
				this.showApplyDetail = false
			},
			handleSubmit() {
				this.$refs.applicationForm.validate(async valid => {
					if (!valid) return
					this.submitLoading = true
					try {
						const submitData = {
							...this.formData,
							applyType: SHOP_APPLY_TYPE.OPEN,
							contactName: this.formData.contactPerson,
							telephone: this.formData.contactPhone,
							subjectName: this.formData.companyName
						}
						await submitMerchantShopApply(submitData)
						this.$message.success('提交成功，请等待平台审核')
						await this.$store.dispatch('merchantShop/loadShopContext', true)
						// 部分环境中新申请不会立即被查询接口返回，先用提交数据更新当前页，避免继续展示旧的驳回记录。
						if (this.applyInfo.id !== submitData.id || Number(this.applyInfo.auditState) !== SHOP_AUDIT_STATE.PENDING) {
							this.$store.commit('merchantShop/SET_LATEST_APPLY', { ...submitData, auditState: SHOP_AUDIT_STATE.PENDING })
						}
						this.showApplyDetail = true
					} finally {
						this.submitLoading = false
					}
				})
			}
		}
	}
</script>

<style lang="scss" scoped>
	.provider-application-page {
		min-height: 100%;
		padding: 24px;
		border: 1px solid #e4e9ef;
		border-radius: 8px;
		background: #ffffff;
		box-sizing: border-box;
	}

	.page-heading,
	.audit-title-row {
		display: flex;
		align-items: center;
		justify-content: space-between;
	}

	.page-heading {
		padding-bottom: 19px;
		border-bottom: 1px solid #e9edf2;

		h2 {
			margin: 0 0 6px;
			color: #303846;
			font-size: 21px;
		}
		p {
			margin: 0;
			color: #929aa6;
			font-size: 13px;
		}
	}

	.audit-card,
	.application-info-card,
	.form-section {
		margin-top: 20px;
		padding: 22px;
		border: 1px solid #e4e9ef;
		border-radius: 8px;
	}

	.audit-card,
	.application-info-card {
		box-shadow: 0 5px 18px rgba(38, 71, 109, 0.04);
	}

	.audit-title-row {
		h3 {
			display: flex;
			align-items: center;
			margin: 0 0 7px;
			color: #303846;
			font-size: 18px;

			&::before {
				width: 4px;
				height: 18px;
				margin-right: 9px;
				border-radius: 2px;
				background: linear-gradient(180deg, #398be2, #216bc1);
				content: '';
			}
		}
		p {
			margin: 0;
			color: #929aa6;
			font-size: 13px;
		}
	}

	.audit-state {
		padding: 6px 13px;
		border-radius: 15px;
		font-size: 13px;

		&.is-pending {
			color: #b47820;
			background: #fff2d9;
		}
		&.is-pass {
			color: #168653;
			background: #e3f6ec;
		}
		&.is-reject {
			color: #d94b4b;
			background: #feebeb;
		}
	}

	.audit-progress {
		display: flex;
		align-items: flex-start;
		max-width: 780px;
		margin: 28px auto 10px;
	}

	.progress-item {
		display: flex;
		align-items: center;
		flex: 0 0 150px;
		flex-direction: column;
		color: #a3aab4;

		.progress-icon {
			display: flex;
			align-items: center;
			justify-content: center;
			width: 46px;
			height: 46px;
			border-radius: 50%;
			font-size: 21px;
			background: #eef0f3;
		}

		strong {
			margin-top: 10px;
			color: #697586;
			font-size: 14px;
		}
		small {
			margin-top: 5px;
			font-size: 12px;
		}

		&.is-complete,
		&.is-current {
			.progress-icon {
				color: #ffffff;
				background: #2671cb;
				box-shadow: 0 5px 12px rgba(38, 113, 203, 0.2);
			}
			strong {
				color: #354154;
			}
		}

		&.is-current .progress-icon {
			animation: audit-pulse 1.8s ease-in-out infinite;
		}
	}

	.progress-line {
		flex: 1;
		height: 2px;
		margin-top: 23px;
		background: #e0e5eb;

		&.is-complete {
			background: #6fa7e4;
		}
	}

	.audit-opinion {
		display: flex;
		margin-top: 24px;
		padding: 15px 17px;
		border: 1px solid #f2c4c4;
		border-radius: 6px;
		color: #ca4545;
		background: #fff7f7;

		> i {
			margin: 3px 10px 0 0;
			font-size: 18px;
		}
		strong {
			display: block;
			font-size: 14px;
		}
		p {
			margin: 6px 0 0;
			color: #805f5f;
			line-height: 22px;
		}
	}

	.application-info-heading {
		display: flex;
		align-items: center;
		justify-content: space-between;
		margin: -4px -4px 22px;
		padding: 4px 4px 17px;
		border-bottom: 1px solid #e7ecf2;

		.heading-main {
			display: flex;
			align-items: center;
		}

		.heading-icon {
			display: flex;
			align-items: center;
			justify-content: center;
			width: 38px;
			height: 38px;
			margin-right: 11px;
			border-radius: 9px;
			color: #2671cb;
			font-size: 19px;
			background: #eaf3fd;
		}

		strong,
		small {
			display: block;
		}

		strong {
			color: #2f3b4d;
			font-size: 18px;
			line-height: 24px;
		}

		small {
			margin-top: 3px;
			color: #98a1ae;
			font-size: 12px;
		}
	}

	.info-grid {
		display: grid;
		grid-template-columns: repeat(2, minmax(0, 1fr));
		gap: 14px 16px;
	}

	.info-item {
		min-width: 0;
		min-height: 76px;
		padding: 14px 16px;
		border: 1px solid #edf0f4;
		border-radius: 6px;
		background: #f8fafc;
		box-sizing: border-box;
		transition: border-color 0.2s ease, box-shadow 0.2s ease, background-color 0.2s ease;

		&.is-full {
			grid-column: 1 / -1;
		}
		&:hover {
			border-color: #cadcf0;
			background: #ffffff;
			box-shadow: 0 4px 13px rgba(36, 91, 150, 0.07);
		}
		span {
			display: block;
			margin-bottom: 8px;
			color: #7e8998;
			font-size: 13px;
		}
		strong {
			color: #344154;
			font-size: 16px;
			font-weight: 600;
			line-height: 25px;
			word-break: break-all;
		}
	}

	.application-form {
		.form-section {
			margin-top: 16px;
			padding: 18px 22px 6px;
			background: #fbfcfd;
		}

		::v-deep .el-form-item {
			margin-bottom: 14px;
		}

		::v-deep .el-form-item__label {
			padding-bottom: 6px;
			color: #566273;
			font-weight: 500;
			line-height: 20px;
		}
	}

	.application-form-heading {
		display: flex;
		align-items: center;
		margin-bottom: 16px;
		padding-bottom: 12px;
		border-bottom: 1px solid #e7edf4;
		color: #2f3b4d;

		&::before {
			width: 4px;
			height: 17px;
			margin-right: 9px;
			border-radius: 2px;
			background: linear-gradient(180deg, #3788df, #216bc1);
			content: '';
		}

		span {
			font-size: 16px;
			font-weight: 600;
		}

		small {
			margin-left: 12px;
			color: #9aa3af;
			font-size: 12px;
			font-weight: normal;
		}
	}

	.form-actions {
		padding: 26px 0 5px;
		text-align: center;

		.el-button {
			min-width: 120px;
		}
	}

	@keyframes audit-pulse {
		0%,
		100% {
			box-shadow: 0 0 0 0 rgba(38, 113, 203, 0.24);
		}
		50% {
			box-shadow: 0 0 0 8px rgba(38, 113, 203, 0);
		}
	}
</style>
